import React, { FC, useState, useEffect } from 'react';
import { Box, Stack, RadioGroup, Typography } from '@mui/material';
import CreateDriver from '../../pages/Drivers/CreateDriver/CreateDriver';
import TwAutocomplete, { TwAutocompleteOption } from '../TwAutocomplete/TwAutocomplete';
import TwRadioButton from '../TwRadioButtons/TwRadioButtons';
import TwTooltip from '../TwTooltip/TwTooltip';
import { Driver } from '../../redux/api/driver/driver.model';
import './TwNotifyDriver.scss';
import { INotifyDriverWithFullSegment } from './TwNotifyDriver';
import { ReactComponent as LinkIcon } from '../../assets/icons/link.svg';
import StringUtils from '../../utils/stringUtils';
import CloseIcon from '@mui/icons-material/Close';

interface INotifySegmentBody {
  driver: INotifyDriverWithFullSegment;
  driverLink: string | null;
  driversList: Driver[];
  handleUpdateDriver: (
    id: string,
    radio: string,
    segmentId: string,
    removed: boolean,
    oldDriverId?: string,
    driverName?: string,
    deletedSegmentIndex?: number
  ) => void;
  allowRemove: boolean;
}

const NotifySegmentsBody: FC<INotifySegmentBody> = ({
  driver,
  driverLink,
  driversList,
  handleUpdateDriver,
  allowRemove
}) => {
  const [selectedDriverOption, setSelectedDriverOption] = useState<TwAutocompleteOption | null>(
    null
  );
  const [selectedDriver, setSelectedDriver] = useState<Driver | undefined>(undefined);
  const [driversListOptions, setDriversListOptions] = useState<TwAutocompleteOption[]>([]);
  const [radio, setRadio] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (driversList.length > 0) {
      const newDrivers: TwAutocompleteOption[] = driversList.map((driver) => ({
        id: driver.id,
        label: `${driver.first_name} ${driver.last_name}`,
        value: driver.id
      }));

      const fullDriverInfo = driversList.find(
        (completedriver) => completedriver.id === driver.driverId
      );

      setSelectedDriver(fullDriverInfo);
      setDriversListOptions(newDrivers);

      handleUpdateDriver(
        fullDriverInfo?.id || '',
        returnRadioOption(fullDriverInfo),
        driver.fullSegment.id || '',
        false,
        undefined,
        fullDriverInfo?.name,
        driver.deleted_trip_index
      );
      if (fullDriverInfo?.phone) {
        setRadio('phone');
      } else {
        fullDriverInfo?.email_address && setRadio('email');
      }
    }
  }, [driversList, driver]);

  useEffect(() => {
    if (driversListOptions.length > 0) {
      const driverOption = driversListOptions.find((driverOpt) => driverOpt.id === driver.driverId);

      setSelectedDriverOption(driverOption || null);
    }
  }, [driversListOptions]);

  const handleCopy = async (): Promise<void> => {
    if (!driverLink) {
      return;
    }

    await StringUtils.copyToClipboard(driverLink);
    setLinkCopied(true);
    setTimeout(() => {
      setLinkCopied(false);
    }, 2000);
  };

  const handleDriverChange = (
    e: any,
    value: TwAutocompleteOption | null,
    removed: boolean,
    oldDriverId?: string
  ): void => {
    const driverInfo = driversList.find((driver) => driver.id === value?.id);

    setSelectedDriver(driverInfo || undefined);

    if (removed) {
      setRadio('');
    } else {
      setSelectedDriverOption(value);

      if (driverInfo?.phone) {
        setRadio('phone');
      } else if (driverInfo?.email_address) {
        setRadio('email');
      } else {
        setRadio('');
      }
    }

    handleUpdateDriver(
      driverInfo?.id || '',
      returnRadioOption(driverInfo),
      driver.fullSegment.id || '',
      removed,
      oldDriverId
    );

    // After the driver is removed and update is sent, we need to clear the autocomplete
    removed && setSelectedDriverOption(null);
  };

  const returnRadioOption = (driverInfo: Driver | undefined): string => {
    if (driverInfo?.phone) {
      return 'phone';
    } else if (driverInfo?.email_address) {
      return 'email';
    }

    return '';
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TwAutocomplete
          id="driver"
          name="driver"
          value={selectedDriverOption || null}
          optionList={driversListOptions}
          disabled={true}
        />
        {allowRemove && (
          <CloseIcon
            className="invoice-items__remove"
            onClick={(): void => {
              handleDriverChange(null, selectedDriverOption, true, selectedDriverOption?.id);
            }}
          />
        )}
      </Box>

      <Stack
        spacing={3}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        className="notify-driver__send-options"
      >
        <RadioGroup
          aria-labelledby="share-load-update-methods"
          name="radiogroup-drivers"
          value={radio}
          defaultValue="phone"
          onChange={(e): void => {
            setRadio(e.target.value);
            handleUpdateDriver(
              selectedDriverOption?.id || '',
              e.target.value,
              driver.fullSegment.id || '',
              false
            );
          }}
          sx={{ flexDirection: 'row' }}
        >
          {!selectedDriver?.phone ? (
            <TwTooltip
              title="Selected driver must have a valid phone number to share via text"
              placement="top-end"
            >
              <Box>
                <TwRadioButton
                  value="phone"
                  onInputChange={(): null => null}
                  name="radio"
                  label="Send as text message"
                  disabled={true}
                  classNames="notify-driver__send-label"
                />
                <span aria-label="tooltip-sms"></span>
              </Box>
            </TwTooltip>
          ) : (
            <TwRadioButton
              name="radio"
              label="Send as text message"
              value="phone"
              onInputChange={(): null => null}
              classNames="notify-driver__send-label"
              disabled={!selectedDriver || !selectedDriverOption || !selectedDriver?.phone}
            />
          )}

          {!selectedDriver?.email_address ? (
            <TwTooltip
              title="Selected driver must have a valid email to share via email"
              placement="top-end"
            >
              <Box>
                <TwRadioButton
                  value="email"
                  onInputChange={(): null => null}
                  name="radio"
                  disabled={true}
                  label="Send via email"
                  classNames="notify-driver__send-label"
                />
                <span aria-label="tooltip-email"></span>
              </Box>
            </TwTooltip>
          ) : (
            <TwRadioButton
              name="radio"
              label="Send via email"
              value="email"
              onInputChange={(e): void => undefined}
              classNames="notify-driver__send-label"
              disabled={!selectedDriver || !selectedDriverOption || !selectedDriver?.email_address}
            />
          )}
        </RadioGroup>
        {driverLink && (
          <span
            className="notify-driver__send-label"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <LinkIcon />
            <Typography
              component={'span'}
              sx={{
                color: '#3A4DE9',
                fontSize: '14px',
                cursor: 'pointer'
              }}
              onClick={(): void => {
                handleCopy();
              }}
            >
              {linkCopied ? 'Link copied!' : 'Copy dispatch link'}
            </Typography>
          </span>
        )}
      </Stack>
    </>
  );
};

export default NotifySegmentsBody;
